import { ReactNode } from 'react'
import type { NextPage, GetStaticPropsContext } from 'next'
import {
  Box,
  Button,
  Center,
  Text,
  Image,
  BoxProps,
  Flex,
} from '@chakra-ui/react'
import NextLink from 'next/link'
import { ArrowForwardIcon } from '@chakra-ui/icons'
import { useTranslations } from 'next-intl'
import { NoiseManagementIcon } from '@/components/icons/NoiseManagementIcon'
import { PowerSupplyIcon } from '@/components/icons/PowerSupplyIcon'
import { SnowFlakeIcon } from '@/components/icons/SnowFlakeIcon'
import { BLOG_URL } from '@/constants'
import SeoHead from '@/components/seo-head'

const AtHomeMiningPage: NextPage = () => {
  const t = useTranslations()

  return (
    <>
      <SeoHead
        title={t('at-home-mining.seo.title')}
        breadcrumbTitle={t('at-home-mining.seo.breadcrumb')}
        description={t('at-home-mining.seo.description')}
      />
      <Box overflow="hidden">
        <HeroSection />
        <MiningGuide />
        <Box
          px={{ base: '5vw', md: '5vw', xl: '6.9444444444vw' }}
          bg="#1E1E1E"
          color="#FFF"
        >
          <Center
            pt={{ base: '10.2564102564vw', md: '3.5340277778vw' }}
            pb={{ base: '', md: '3.5340277778vw' }}
            flexDir="column"
          >
            <Text
              fontWeight="600"
              fontSize={{ base: '7.6923076923vw', md: '38px', lg: '48px' }}
              lineHeight={{ base: '12.3076923077vw', md: '58px' }}
            >
              {t('at-home-mining.prepare-home.title')}
            </Text>
            <Text
              textAlign="center"
              mt={{ base: '5px', md: '6.22px' }}
              fontSize={{ base: '14px', md: '20px', lg: '24px' }}
              lineHeight={{ base: 'medium', md: '29px' }}
            >
              {t('at-home-mining.prepare-home.description')}
            </Text>
          </Center>
          <SectionContainer>
            <NoiseManagement />
          </SectionContainer>
          <SectionContainer>
            <PowerSupply />
          </SectionContainer>
          <SectionContainer>
            <CoolingSystem />
          </SectionContainer>
        </Box>
        <Box bg="gradient.brand">
          <SectionContainer>
            <JoiningAPool />
          </SectionContainer>
        </Box>
        <SectionContainer
          pl={{ base: '16px', xl: '32px' }}
          pr={{ base: '16px', xl: '48px' }}
        >
          <Maintenance />
        </SectionContainer>
        <SectionContainer
          pl={{ base: '16px', md: '48px', '2xl': 'unset' }}
          pr={{ base: '16px', md: '48px', '2xl': 'unset' }}
        >
          <ShopMiningHardware />
        </SectionContainer>
      </Box>
    </>
  )
}

interface ISectionBox extends BoxProps {
  children: ReactNode
}

const SectionContainer = ({ children, ...rest }: ISectionBox) => {
  return (
    <Center {...rest}>
      <Box width="100%" maxW={{ base: '', xl: '1240px' }}>
        {children}
      </Box>
    </Center>
  )
}

const HeroSection = () => {
  const t = useTranslations()
  return (
    <Box
      bg="gradient.brand"
      height={{ base: '45vw', md: '338px' }}
      color="#FFF"
      position="relative"
      zIndex="-1"
      overflow="hidden"
    >
      <Center
        height={{ base: '100%', md: 'unset' }}
        flexDirection="column"
        pt={{ base: '', md: '90px' }}
      >
        <Text fontSize={{ base: '5vw', md: '24px' }} fontWeight="500">
          {t('at-home-mining.hero.title')}
        </Text>
        <Text fontSize={{ base: '7.7vw', md: '68px' }} fontWeight="600">
          {t('at-home-mining.hero.description')}
        </Text>
      </Center>
      <Box
        width={{ base: '', md: '568.2px' }}
        height={{ base: '', md: '510.69px' }}
        position="absolute"
        right={{ base: '', md: '-6.6666666667vw' }}
        top={{ base: '', md: '-2.4305555556vw' }}
        display={{ base: 'none', md: 'block' }}
      >
        <Image
          height="100%"
          width="100%"
          objectFit="contain"
          src="/img/home.png"
          alt="home"
        />
      </Box>
    </Box>
  )
}

const MiningGuide = () => {
  const t = useTranslations()

  return (
    <Box
      pt={{ base: '6vw', md: '43px' }}
      pb={{ base: '15vw', md: '10vw', xl: '26px' }}
      pl={{ base: '5vw', xl: '12.1527777778vw' }}
      pr={{ base: '5vw', xl: '9.0277777778vw' }}
      display="flex"
      alignItems="center"
      justifyContent="center"
    >
      <Flex flexWrap={{ base: 'wrap', xl: 'unset' }} justifyContent="center">
        <Box w="full" maxW="383px" mr={{ base: '', md: '58px' }}>
          <Image
            src="/img/book-mockutrans.png"
            height="100%"
            width="100%"
            objectFit="contain"
            alt="book"
          />
        </Box>
        <Box pt={{ base: '', lg: '38px' }} width={{ base: '', xl: '694px' }}>
          <Text
            color="brand.600"
            fontWeight="700"
            fontSize={{ base: 'sm', md: 'md', xl: '14px' }}
            lineHeight={{ base: '6.1538461538vw', md: '26px' }}
            textAlign={{ base: 'center', md: 'unset' }}
            as="h2"
          >
            {t('at-home-mining.mining-guide.subtitle')}
          </Text>
          <Text
            mt={{ base: '5vw', md: '14px' }}
            textAlign={{ base: 'center', md: 'unset' }}
            fontSize={{ base: '6.1538461538vw', md: '36px' }}
            fontWeight="600"
            lineHeight={{ base: '', md: '44px' }}
            variant="withDarkMode"
            as="h1"
          >
            {t('at-home-mining.mining-guide.title')}
          </Text>
          <Text
            mt={{ base: '2vw', md: '15px' }}
            fontSize={{ base: 'xs', md: 'sm', lg: '14px' }}
            lineHeight={{ base: 'xs', md: '24px' }}
            variant="withDarkMode"
          >
            <span>{t('at-home-mining.mining-guide.description.block1')}</span>
            <br />
            <br />
            <span>{t('at-home-mining.mining-guide.description.block2')}</span>
          </Text>
          <Button
            mt={{ base: '8vw', md: '28px' }}
            variant="brand"
            width={{ base: '100%', md: '258px' }}
            height="50px"
            as="a"
            href={`${BLOG_URL}/homeminingguide`}
          >
            {t('buttons.download-guide')}
          </Button>
        </Box>
      </Flex>
    </Box>
  )
}

const NoiseManagement = () => {
  const t = useTranslations()

  return (
    <Flex
      pt={{ base: '10px', md: '50px', xl: '83.52px' }}
      pb={{ base: '', md: '85.49px' }}
      alignItems={{ base: 'center', xl: 'unset' }}
      justifyContent={{ base: 'center', xl: 'space-between' }}
      flexDirection={{ base: 'column-reverse', xl: 'unset' }}
    >
      <Box
        pt={{ base: '', md: '50px', xl: '20px' }}
        pr={{ base: '', md: '4vw' }}
      >
        <Flex alignItems="center">
          <Box
            bg="brand.600"
            boxSize={{ base: '35px', md: '54px', xl: '64px' }}
            borderRadius={{ base: 'lg', md: '10px', xl: '19px' }}
            padding={{ base: '1vw', md: '14px' }}
            mr={{ base: '4vw', md: '27.5px' }}
          >
            <NoiseManagementIcon
              fill="white"
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Text
            fontWeight="600"
            fontSize={{ base: '6.15vw', md: '40px', xl: '48px' }}
          >
            {t('at-home-mining.noise-management.title')}
          </Text>
        </Flex>
        <Text
          mt={{ base: '5.12vw', md: '29.3px' }}
          width={{ base: '', md: '460px', xl: '667px' }}
          fontSize={{ base: '14px', md: '18px', xl: '20px' }}
          lineHeight={{ base: '24px', md: '30px', xl: '24px' }}
        >
          {t('at-home-mining.noise-management.description.block1')}
          <br />
          <br />
          {t('at-home-mining.noise-management.description.block2')}
          <br />
          <br />
          {t('at-home-mining.noise-management.description.block3')}
        </Text>
      </Box>
      <Box
        my={{ base: '10vw', md: 'unset' }}
        width={{ base: '45.89vw', md: '357px', xl: '438.5px' }}
        height={{ base: '45.89vw', md: '291px', xl: '431.68px' }}
      >
        <Image
          width="100%"
          height="100%"
          objectFit="contain"
          src="/img/soundgrid.png"
          alt="soundgrid"
        />
      </Box>
    </Flex>
  )
}

const PowerSupply = () => {
  const t = useTranslations()

  return (
    <Flex
      pt={{ base: '', xl: '98px' }}
      pb={{ base: '', xl: '85.49px' }}
      alignItems={{ base: 'center', xl: 'unset' }}
      justifyContent={{ base: 'center', xl: 'space-between' }}
      flexDirection={{ base: 'column', xl: 'unset' }}
    >
      <Box
        mt={{ base: '', md: '14.4px' }}
        width={{ base: '45.89vw', md: '357px', xl: '384px' }}
        height={{ base: '45.89vw', md: '291px', xl: '396px' }}
        mx={{ base: '', xl: '43.2px' }}
        my={{ base: '10vw', md: 'unset' }}
      >
        <Image
          width="100%"
          height="100%"
          objectFit="contain"
          src="/img/power-supply.png"
          alt="power-supply"
        />
      </Box>
      <Box
        width={{ base: '', md: '467px', xl: '667px' }}
        mb={{ base: '5vw', xl: 'unset' }}
        pt={{ base: 'unset', md: '50px', xl: 'unset' }}
      >
        <Flex alignItems="center">
          <Box
            bg="#FEDA5C"
            boxSize={{ base: '35px', md: '54px', xl: '64px' }}
            borderRadius={{ base: 'lg', md: '10px', xl: '19px' }}
            padding={{ base: '1vw', md: '14px' }}
            mr={{ base: '4vw', md: '1.9097222222vw' }}
          >
            <PowerSupplyIcon
              fill="white"
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Text
            fontWeight="600"
            fontSize={{ base: '6.15vw', md: '40px', xl: '48px' }}
          >
            {t('at-home-mining.power-supply.title')}
          </Text>
        </Flex>
        <Text
          mt={{ base: '5.12vw', md: '18.72px' }}
          width={{ base: '', xl: '667px' }}
          fontSize={{ base: '14px', md: '18px', xl: '20px' }}
          lineHeight={{ base: '24px', md: '30px', xl: '24px' }}
        >
          {t('at-home-mining.power-supply.description.block1')}
          <br />
          <br />
          {t('at-home-mining.power-supply.description.block2')}
          <br />
          <br />
          {t('at-home-mining.power-supply.description.block3')}
        </Text>
      </Box>
    </Flex>
  )
}

const CoolingSystem = () => {
  const t = useTranslations()
  return (
    <Flex
      pt={{ base: '', md: '', xl: '83.52px' }}
      pb={{ base: '15vw', md: '79.49px' }}
      alignItems={{ base: 'center', xl: 'unset' }}
      justifyContent={{ base: 'center', xl: 'space-between' }}
      flexDirection={{ base: 'column-reverse', xl: 'unset' }}
    >
      <Box pt={{ base: '', xl: '13px' }} pr={{ base: '', md: '4vw' }}>
        <Flex alignItems="center">
          <Box
            bg="#3F89F6"
            boxSize={{ base: '35px', md: '54px', xl: '64px' }}
            borderRadius={{ base: 'lg', md: '10px', xl: '19px' }}
            padding={{ base: '1vw', md: '14px' }}
            mr={{ base: '4vw', md: '1.9097222222vw' }}
          >
            <SnowFlakeIcon
              fill="white"
              style={{ width: '100%', height: '100%' }}
            />
          </Box>
          <Text
            fontWeight="600"
            fontSize={{ base: '6.15vw', md: '40px', xl: '48px' }}
          >
            {t('at-home-mining.cooling-system.title')}
          </Text>
        </Flex>
        <Text
          mt={{ base: '5.12vw', md: '18.72px' }}
          width={{ base: '', md: '460px', xl: '667px' }}
          fontSize={{ base: '14px', md: '18px', xl: '20px' }}
          lineHeight={{ base: '24px', md: '30px', xl: '24px' }}
        >
          {t('at-home-mining.cooling-system.description.block1')}
          <br />
          <br />
          {t.rich('at-home-mining.cooling-system.description.block2', {
            link: (children) => (
              <Text
                color="#EE6F43"
                textDecoration="underline"
                as="a"
                href="https://www.vice.com/en/article/9k8ykp/heating-water-with-bitcoin-mining-asic-reddit"
                target="_blank"
                _hover={{
                  color: 'brand.500',
                }}
              >
                {children}
              </Text>
            ),
          })}
          <br />
          <br />
          {t.rich('at-home-mining.cooling-system.description.block3', {
            link: (children) => (
              <Text
                color="#EE6F43"
                textDecoration="underline"
                as="a"
                href="https://twitter.com/Betahedged/status/1428044220665896967"
                target="_blank"
                _hover={{
                  color: 'brand.500',
                }}
              >
                {children}
              </Text>
            ),
          })}
        </Text>
      </Box>
      <Box
        my={{ base: '10vw', md: '43.4px' }}
        mr={{ base: '', xl: '43.2px' }}
        width={{ base: '45.89vw', md: '357px', xl: '368px' }}
        height={{ base: '45.89vw', md: '291px', xl: '368px' }}
      >
        <Image
          width="100%"
          height="100%"
          objectFit="contain"
          src="/img/cooling-system.png"
          alt="cooling-system"
        />
      </Box>
    </Flex>
  )
}

const StepCircle = ({ children }: { children: ReactNode }) => (
  <Flex
    fontWeight="600"
    fontFamily="'Inter'"
    fontSize={{ base: '18px', lg: '24px' }}
    minW={{ base: '35px', md: '35px', lg: '40px' }}
    minH={{ base: '35px', md: '35px', lg: '40px' }}
    bg="#FFF"
    color="brand.600"
    borderRadius="full"
    alignItems="center"
    justifyContent="center"
  >
    {children}
  </Flex>
)
const Step = ({ children, step }: { children: ReactNode; step: string }) => (
  <Flex
    flexDir={{ base: 'row', md: 'column' }}
    alignItems="center"
    my={{ base: '3vw', md: 'unset' }}
    zIndex="1"
  >
    <StepCircle>{step}</StepCircle>
    <Box
      mt={{ base: '', md: '15px', lg: '30.66px' }}
      textAlign="center"
      fontSize={{ base: '14px', lg: '18px' }}
      lineHeight={{ base: '18px', md: '22px' }}
      maxW={{ base: '250px', md: '160px', lg: '230px' }}
    >
      <Text
        ml={{ base: '5.12vw', md: 'unset' }}
        textAlign={{ base: 'left', md: 'center' }}
      >
        {children}
      </Text>
    </Box>
  </Flex>
)
const JoiningAPool = () => {
  const t = useTranslations()
  return (
    <Box
      color="#FFF"
      height={{ base: 'auto', xl: '601px' }}
      px={{ base: '5vw', xl: 'unset' }}
      pb={{ base: '8vw', md: '5vw', xl: 'unset' }}
    >
      <Center flexDir="column" width="100%">
        <Text
          mt={{ base: '30px', md: '44.64px' }}
          fontWeight="600"
          fontSize={{ base: '28px', md: '38px', lg: '48px' }}
        >
          {t('at-home-mining.joining-pool.title')}
        </Text>
        <Text
          mt={{ base: '2vw', md: '5px', lg: '7.2px' }}
          fontSize={{ base: '15px', md: '18px', lg: '24px' }}
          lineHeight={{ base: '', md: '29px' }}
          textAlign="center"
          maxW="911px"
        >
          {t('at-home-mining.joining-pool.description.block1')}
          <br />
          <br />
          <b>{t('at-home-mining.joining-pool.description.block2')}</b>
        </Text>
        <Flex
          width={{ base: '75vw', md: '700px', lg: '1017px' }}
          mt={{ base: '5vw', md: '43px', lg: '63.36px' }}
          justifyContent="space-between"
          alignItems={{ base: 'center', md: 'unset' }}
          position="relative"
          flexDirection={{ base: 'column', md: 'row' }}
          overflow="hidden"
        >
          <Box
            width={{ md: '85%', lg: '100%' }}
            position="absolute"
            top="19px"
            zIndex="0"
            pl={{ md: '15%', lg: '12%' }}
            display={{ base: 'none', md: 'block' }}
          >
            <svg
              style={{ width: '100%', height: '100%' }}
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M0 0.95459H760" stroke="#BCBCBC" strokeDasharray="9 9" />
            </svg>
          </Box>
          <Step step="1">
            {t.rich('at-home-mining.joining-pool.steps.step1', {
              link: (children) => (
                <Text
                  textDecoration="underline"
                  as="a"
                  href={`${BLOG_URL}/how-to-mine-bitcoin-at-home-basics`}
                >
                  <br />
                  {children}
                </Text>
              ),
            })}
          </Step>
          <Step step="2">{t('at-home-mining.joining-pool.steps.step2')}</Step>
          <Step step="3">{t('at-home-mining.joining-pool.steps.step3')}</Step>
          <Step step="4">{t('at-home-mining.joining-pool.steps.step4')}</Step>
        </Flex>
        <Text
          mt={{ base: '10.25vw', md: '35px', lg: '50.4px' }}
          fontStyle="italic"
          fontSize={{ base: '14px', md: '18px', lg: '24px' }}
          textAlign={{ base: 'center', md: 'unset' }}
        >
          {t('at-home-mining.joining-pool.note')}
        </Text>
      </Center>
    </Box>
  )
}

const Maintenance = () => {
  const t = useTranslations()
  return (
    <Flex
      pt={{ base: '10vw', xl: '120px' }}
      pb={{ base: '10vw', xl: '85.49px' }}
      alignItems={{ base: 'center', xl: 'unset' }}
      justifyContent={{ base: 'center', xl: 'space-between' }}
      flexDirection={{ base: 'column-reverse', xl: 'unset' }}
    >
      <Box pl={{ base: '', md: '1vw' }}>
        <Text
          variant="withDarkMode"
          py={{ base: '3vw', xl: 'unset' }}
          fontWeight="600"
          fontSize={{ base: '36px', md: '40px', xl: '48px' }}
          lineHeight={{ base: '', md: '58px' }}
        >
          {t('at-home-mining.maintenance.title')}
        </Text>
        <Text
          variant="withDarkMode"
          mt={{ base: '', xl: '45px' }}
          fontSize={{ base: '14px', md: '18px', xl: '20px' }}
          lineHeight={{ base: '24px', md: '30px', xl: '24px' }}
          width={{ base: '', md: '500px', xl: '590.69px' }}
        >
          {t('at-home-mining.maintenance.description.block1')}
          <br />
          <br />
          {t('at-home-mining.maintenance.description.block2')}
          <br />
          <br />
          {t('at-home-mining.maintenance.description.block3')}
        </Text>
      </Box>
      <Box>
        <Image
          width={{ base: '357px', xl: '508px' }}
          height={{ base: '291px', xl: '412.67px' }}
          src="/img/maintenance.png"
          alt="maintenance"
        />
      </Box>
    </Flex>
  )
}

const ShopMiningHardware = () => {
  const t = useTranslations()
  return (
    <Box
      bg="#000"
      color="#FFF"
      borderRadius={{ base: '16px', md: '43px' }}
      // ml={{ base: '', xl: '1.2vw' }}
      mt={{ base: '', xl: '4.4vw' }}
      padding={{ base: '32px 16px', md: '30px 30px', xl: '65px 112px' }}
      height={{ base: '415px', md: '342px', xl: '442px' }}
      position="relative"
    >
      <Text
        width={{ base: '', md: '642px' }}
        fontSize={{ base: '20px', md: '44px', xl: '64px' }}
        lineHeight={{ base: '25px', md: '57px', xl: '77px' }}
        mb={{ base: '32px', md: '31px', xl: '41px' }}
      >
        {t('at-home-mining.shop-hardware.title')}
      </Text>
      {t.rich('at-home-mining.shop-hardware.shop-link', {
        link: (children) => (
          <Text
            cursor="pointer"
            color="brand.600"
            fontSize={{ base: '22px', md: '36px' }}
            _hover={{
              color: 'brand.500',
            }}
          >
            <NextLink href="/hardware">
              <Text as="span">{children}</Text>
            </NextLink>
            <ArrowForwardIcon />
          </Text>
        ),
      })}
      <Box
        position="absolute"
        right="0"
        bottom="0"
        width={{ base: '250px', md: '250px', xl: '390px' }}
        borderRadius={{ base: '16px', md: '43px' }}
      >
        <Image
          width="100%"
          height="100%"
          objectFit="contain"
          src="/img/units.png"
          borderRadius={{ base: '16px', md: '43px' }}
          alt="units"
        />
      </Box>
    </Box>
  )
}

export async function getStaticProps({ locale }: GetStaticPropsContext) {
  return {
    props: {
      messages: (await import(`../../locales/${locale}.json`)).default,
    },
  }
}

export default AtHomeMiningPage
